import { useEffect, useMemo, useState } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-markup";
import { CodeCopyButton } from "./CodeCopyButton";
import { toSafeCodeHtml } from "./code-display-utils";
import { formatCodeForDisplay, type CodeFormatLanguage } from "./code-formatting";

interface CodeDisplayProps {
  code: string;
  language?: CodeFormatLanguage;
  maxHeight?: string;
  showCopyButton?: boolean;
  className?: string;
}

export function CodeDisplay({
  code,
  language = "javascript",
  maxHeight = "300px",
  showCopyButton = true,
  className = "",
}: CodeDisplayProps) {
  const [formattedCode, setFormattedCode] = useState(code);

  useEffect(() => {
    let cancelled = false;
    setFormattedCode(code);
    formatCodeForDisplay(code, language).then((next) => {
      if (!cancelled) setFormattedCode(next);
    });
    return () => {
      cancelled = true;
    };
  }, [code, language]);

  // Apply Prism highlighting - generate HTML directly
  const highlightedHtml = useMemo(() => {
    if (!formattedCode?.trim()) return "";
    try {
      let html = formattedCode;
      const prismLang = Prism.languages[language];
      if (prismLang) {
        html = Prism.highlight(formattedCode, prismLang, language);
      } else {
        return toSafeCodeHtml(formattedCode);
      }
      return html;
    } catch {
      return toSafeCodeHtml(formattedCode);
    }
  }, [formattedCode, language]);

  if (!code?.trim()) {
    return <div className="text-sm text-gray-400 italic p-3">Không có code</div>;
  }

  return (
    <div className={`relative ${className}`}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
        {showCopyButton && (
          <div className="flex shrink-0 justify-end border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
            <CodeCopyButton value={formattedCode} variant="inline" />
          </div>
        )}
        <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight }}>
          <pre className="m-0! p-3! font-mono text-xs leading-relaxed whitespace-pre-wrap! break-words sm:p-4! sm:text-sm">
            <code
              className={`language-${language} block whitespace-pre-wrap! break-words`}
              dangerouslySetInnerHTML={{ __html: highlightedHtml }}
            />
          </pre>
        </div>
      </div>
    </div>
  );
}
